import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';

export interface MlReputation {
  level_id: string | null;
  power_seller_status: string | null;
  transactions?: {
    total?: number;
    ratings?: { positive?: number; neutral?: number; negative?: number };
  };
  metrics?: Record<string, unknown>;
}

export interface MlMetrics {
  reputation: MlReputation | null;
  visits_30d: Array<{ date: string; total: number }> | null;
  visits_total_30d: number | null;
  questions_open: number | null;
  low_stock_items: Array<{ id: string; title: string; available_quantity: number }> | null;
  updated_at: string;
}

const STALE_MS = 30 * 60 * 1000; // 30 min

/** Lê ml_metrics e dispara o ml-sync-metrics quando os dados estão velhos. */
export function useMlMetrics() {
  const { tenantId } = useTenant();
  const [metrics, setMetrics] = useState<MlMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setLoading(false); return; }
    let cancelled = false;

    async function load(afterSync = false) {
      const { data } = await supabase
        .from('ml_metrics')
        .select('reputation, visits_30d, visits_total_30d, questions_open, low_stock_items, updated_at')
        .eq('tenant_id', tenantId!)
        .maybeSingle();
      if (cancelled) return;
      const row = data as MlMetrics | null;
      setMetrics(row);
      setLoading(false);

      const stale = !row || Date.now() - new Date(row.updated_at).getTime() > STALE_MS;
      if (stale && !afterSync) {
        // fire-and-forget: sincroniza e relê
        supabase.functions.invoke('ml-sync-metrics', { body: {} })
          .then(() => { if (!cancelled) void load(true); })
          .catch(() => { /* sem conexão ML ou erro — mantém o que há */ });
      }
    }

    void load();
    return () => { cancelled = true; };
  }, [tenantId]);

  return { metrics, loading };
}

/** Converte level_id da reputação ML ("5_green" etc.) em rótulo amigável. */
export function reputationLabel(rep: MlReputation | null | undefined): { label: string; color: string } {
  if (!rep?.level_id) return { label: '—', color: 'gray' };
  const [levelRaw, colorRaw] = String(rep.level_id).split('_');
  const level = Number(levelRaw);
  const names: Record<string, string> = {
    green: 'Verde', yellow: 'Amarela', orange: 'Laranja', red: 'Vermelha', light_green: 'Verde-clara',
  };
  return {
    label: `${Number.isFinite(level) ? `${level}/5 ` : ''}${names[colorRaw ?? ''] ?? rep.level_id}`,
    color: colorRaw ?? 'gray',
  };
}
